import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import type { ResendTreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

interface UseResendRequestParams {
  matterId: string;
  providerId: string;
  requestId: string;
}

interface UseResendRequestResult {
  resend: (payload: ResendTreatmentRecordRequestSchema) => Promise<void>;
  isResending: boolean;
}

export const useResendRequest = ({
  matterId,
  providerId,
  requestId,
}: UseResendRequestParams): UseResendRequestResult => {
  const dispatch = useDispatch();
  const [resendRequest, { isLoading: isResending }] =
    treatmentRecordRequestService.useResendRecordRequestMutation();

  const resend = useCallback(
    async (payload: ResendTreatmentRecordRequestSchema) => {
      try {
        await resendRequest({
          matterId,
          providerId,
          requestId,
          resendTreatmentRecordRequestSchema: payload,
        }).unwrap();

        toast.success('Resend request submitted successfully.');

        dispatch(
          treatmentRecordRequestService.util.invalidateTags([
            { type: 'record-request', id: requestId },
            { type: 'record-requests', id: providerId },
          ]),
        );
      } catch (error) {
        const message =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'error' in (error.data as Record<string, unknown>)
            ? ((error.data as { error?: string }).error ??
              'Failed to submit resend request.')
            : 'Failed to submit resend request.';

        toast.error(message);
        throw error;
      }
    },
    [dispatch, matterId, providerId, requestId, resendRequest],
  );

  return { resend, isResending };
};
